import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { CATEGORY_FIELD_GROUPS } from '@features/onboarding/components/shared/form-plan/constants/plans';
import { getPlanLogo } from '@features/onboarding/components/shared/form-plan/planLogo';
import { buildKeyedPathResolver } from '@features/onboarding/utils/pathResolver';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { useCompareStore } from 'src/store/compare';

import { cn, isLollySkin } from '@utils/index';

const formatNumberToEuropean = (value: string | number): string => {
  if (typeof value === 'number') {
    return `$${value.toLocaleString('en-US')}`;
  }

  if (typeof value === 'string') {
    const cleanValue = value.replace(/[$,]/g, '');
    const numValue = parseFloat(cleanValue);
    if (!isNaN(numValue)) {
      return `$${numValue.toLocaleString('en-US')}`;
    }

    return value;
  }

  return String(value);
};

const formatCurrency = (value: string | number): string => {
  if (typeof value === 'number') {
    return `$${value.toLocaleString('en-US')}`;
  }

  if (typeof value === 'string') {
    const cleanValue = value.replace(/[$,]/g, '');
    const numValue = parseFloat(cleanValue);
    if (!isNaN(numValue)) {
      return `$${numValue.toLocaleString('en-US')}`;
    }
    return value;
  }

  return String(value);
};

export type Plan = {
  id: string;
  title: string;
  image: string;
  planData?: InsurancePlan; // real plan data (optional)
};

export type ComparePlansModalProps = {
  plans: Plan[]; // initially selected plans (coming from calling component)
  onRemovePlan: (id: string) => void;
  onAddPlan: () => void;
  onReplacePlan?: (oldId: string, newId: string) => void;
  onClose: () => void;
  allPlans?: InsurancePlan[]; // full list of plans to choose from
  selectedPlanIdsLocal?: string[]; // full list of plans to choose from
};

export const ComparePlansModal = ({
  plans: initialPlans,
  onRemovePlan,
  onAddPlan,
  onReplacePlan,
  onClose,
  allPlans = [],
  selectedPlanIdsLocal,
}: ComparePlansModalProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const { selectedPlans: selectedPlanIds, removePlan } = useCompareStore();

  const selectedPlans = (
    selectedPlanIdsLocal?.length ? selectedPlanIdsLocal : selectedPlanIds
  )
    .map((planId) => {
      const plan = initialPlans.find((p) => p.id === planId);
      if (plan) {
        const realPlan = allPlans.find((p) => p.uid === planId);
        if (realPlan) {
          return {
            ...plan,
            image: getPlanLogo(realPlan),
            planData: realPlan,
          } as Plan;
        }
        return plan;
      }

      const realPlan = allPlans.find((p) => p.uid === planId);
      if (realPlan) {
        return {
          id: realPlan.uid,
          title: realPlan.name || realPlan.marketingName || 'Untitled',
          image: getPlanLogo(realPlan),
          planData: realPlan,
        };
      }
      return null;
    })
    .filter(Boolean) as Plan[];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePlanChange = (oldPlanId: string, newPlanUid: string) => {
    if (oldPlanId === newPlanUid) return;

    const realPlan = allPlans.find((p) => p.uid === newPlanUid);
    if (!realPlan) return;

    if (onReplacePlan) {
      onReplacePlan(oldPlanId, newPlanUid);
    }
  };

  const generateComparisonData = () => {
    const comparisonData: {
      label: string;
      values: Record<string, string | number>;
    }[] = [];

    selectedPlans.forEach((plan) => {
      const realPlan = allPlans.find((p) => p.uid === plan.id);
      if (realPlan) {
        const fieldGroups = CATEGORY_FIELD_GROUPS[realPlan.category] || [];

        fieldGroups.forEach((group) => {
          group.items.forEach((item) => {
            const resolve = buildKeyedPathResolver(realPlan);
            const { value } = resolve(item);

            let comparisonItem = comparisonData.find(
              (ci) => ci.label === item.label
            );
            if (!comparisonItem) {
              comparisonItem = {
                label: item.label,
                values: {},
              };
              comparisonData.push(comparisonItem);
            }

            let formattedValue = value;

            const isNumeric = (val: string | number): boolean => {
              if (typeof val === 'number') return true;
              if (typeof val === 'string') {
                const num = parseFloat(val);
                return !isNaN(num) && isFinite(num);
              }
              return false;
            };

            if (isNumeric(value)) {
              formattedValue = formatCurrency(value);
            }

            comparisonItem.values[plan.id] = formattedValue;
          });
        });
      }
    });

    comparisonData.forEach((item) => {
      selectedPlans.forEach((plan) => {
        if (!(plan.id in item.values)) {
          item.values[plan.id] = '—';
        }
      });
    });

    return comparisonData;
  };

  const comparisonData = generateComparisonData();
  const visiblePlans = isMobile ? selectedPlans.slice(0, 2) : selectedPlans;

  if (isMobile) {
    return (
      <div className={cn("relative rounded p-0 m-0 lg:shadow lg:p-4 text-sm", !isLollySkin() && "bg-white")}>
        <button
          onClick={onClose}
          className="absolute right-2 top-0 lg:top-8.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
          aria-label="Close comparison"
        >
          CLOSE
        </button>
        <div className="mb-6 mt-2 lg:mt-16 overflow-auto">
          <div className="font-bold text-gray-400 mb-2">Insurer</div>
          <div className="grid grid-cols-2 gap-6">
            {visiblePlans.map((plan) => (
              <div key={plan.id} className="relative text-center">
                {!selectedPlanIdsLocal?.length && (
                  <button
                    onClick={() => {
                      removePlan(plan.id);
                      onRemovePlan(plan.id);
                      if (selectedPlans.length === 1) {
                        setTimeout(onClose, 0);
                      }
                    }}
                    className="absolute -top-8 left-[45%] text-red-600 hover:text-red-800 text-xs font-extrabold"
                    aria-label={`Remove plan ${plan.title}`}
                  >
                    ✕
                  </button>
                )}
                <img
                  src={plan.image}
                  alt={plan.title}
                  className="mx-auto h-12 w-auto object-contain"
                />
              </div>
            ))}
            {Array.from({ length: 2 - visiblePlans.length }, (_, index) => (
              <div key={`mobile-plus-${index}`} className="text-center">
                {!selectedPlanIdsLocal?.length && (
                  <button
                    onClick={onAddPlan}
                    className="cursor-pointer bg-gray-200 p-1.5 rounded-lg"
                  >
                    <img
                      src="/images/plus-plan.png"
                      alt="Add plan"
                      className="mx-auto h-5 w-5"
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className="font-bold text-gray-400 mb-2">Plan Name</div>
          <div className="grid grid-cols-2 gap-6 text-center font-semibold">
            {visiblePlans.map((plan) => (
              <div key={plan.id} className="relative">
                {(() => {
                  const options = allPlans.filter((p) => {
                    const cat = allPlans.find(
                      (ap) => ap.uid === plan.id
                    )?.category;
                    if (p.category !== cat) return false;
                    if (p.uid === plan.id) return true;
                    return !selectedPlanIds.includes(p.uid);
                  });
                  return (
                    <>
                      <select
                        value={plan.id}
                        onChange={(e) =>
                          handlePlanChange(plan.id, e.target.value)
                        }
                        className="appearance-none bg-transparent w-full text-center cursor-pointer"
                        disabled={options.length <= 1}
                      >
                        {options.map((p) => (
                          <option key={p.uid} value={p.uid}>
                            {p.name || p.marketingName || 'Untitled'}
                          </option>
                        ))}
                      </select>
                      {options.length > 1 && (
                        <span className="h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <ChevronDownIcon className="w-3 h-3" />
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            ))}
            {Array.from({ length: 2 - visiblePlans.length }, (_, index) => (
              <div key={`mobile-empty-${index}`}></div>
            ))}
          </div>
        </div>

        {comparisonData.map(({ label, values }) => (
          <div key={label} className="mb-6">
            <div className="font-bold text-gray-400 mb-2">{label}</div>
            <div className="grid grid-cols-2 gap-6 text-center">
              {visiblePlans.map((plan) => (
                <div key={plan.id + label}>{values[plan.id] || '—'}</div>
              ))}
              {Array.from({ length: 2 - visiblePlans.length }, (_, index) => (
                <div key={`mobile-comparison-empty-${label}-${index}`}></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  /* ------------------------------------------------------------------
   *  Desktop layout
   * ----------------------------------------------------------------*/
  return (
    <>
      <button
        onClick={onClose}
        className={cn(
          "absolute top-3 right-7 z-11111 px-1 text-black hover:text-gray-900 text-xs font-semibold cursor-pointer",
          !isLollySkin() && "bg-white"
        )}
        aria-label="Close comparison"
      >
        CLOSE
      </button>
      <div className="relative">
        {!isLollySkin() && (
          <div className="plane-compare-line w-full sticky top-0 right-0 h-3 bg-linear-to-b from-white from-30% to-transparent max-2xl:hidden z-20"></div>
        )}
        <div className="divide-y divide-gray-200 w-full overflow-auto relative z-10000">
          <div className="grid grid-cols-[minmax(200px,1fr)_repeat(3,minmax(120px,1fr))] gap-4 py-3 items-center">
            <div className="font-medium h-full flex flex-col text-xs">
              <span className="py-3 mt-auto">Insurer</span>
            </div>
            {selectedPlans.map((plan) => (
              <div
                key={plan.id + '-insurer'}
                className="text-center flex flex-col gap-2 relative"
              >
                {!selectedPlanIdsLocal?.length && (
                  <button
                    onClick={() => {
                      removePlan(plan.id);
                      onRemovePlan(plan.id);
                      if (selectedPlans.length === 1) {
                        setTimeout(onClose, 0);
                      }
                    }}
                    className="cursor-pointer font-extrabold text-red-600 hover:text-red-800"
                    aria-label={`Remove plan ${plan.title}`}
                  >
                    ✕
                  </button>
                )}
                <img
                  src={plan.image}
                  alt={plan.title}
                  className="mx-auto h-16 w-16 object-contain"
                />
              </div>
            ))}
            {Array.from({ length: 3 - selectedPlans.length }, (_, index) => (
              <div
                key={`empty-slot-${index}`}
                className="text-center max-2xl:hidden"
              >
                {!selectedPlanIdsLocal?.length && (
                  <button
                    onClick={onAddPlan}
                    className="cursor-pointer bg-gray-200 p-1.5 rounded-lg"
                  >
                    <img
                      src="/images/plus-plan.png"
                      alt="Add plan"
                      className="mx-auto h-5 w-5"
                    />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-[minmax(200px,1fr)_repeat(3,minmax(120px,1fr))] gap-4 py-3 items-center">
            <div className="font-medium text-xs text-gray-700">Plan Name</div>
            {selectedPlanIdsLocal?.length ? (
              <>
                {selectedPlans.map((plan) => (
                  <div
                    key={plan.id + '-title'}
                    className="flex items-center gap-2 text-xs font-semibold relative mx-auto select-none"
                  >
                    {plan.title || 'Untitled'}
                  </div>
                ))}
              </>
            ) : (
              selectedPlans.map((plan) => (
                <div
                  key={plan.id + '-title'}
                  className="flex items-center gap-2 text-xs font-semibold relative mx-auto select-none"
                >
                  {(() => {
                    const options = allPlans.filter((p) => {
                      const cat = allPlans.find(
                        (ap) => ap.uid === plan.id
                      )?.category;
                      if (p.category !== cat) return false;
                      if (p.uid === plan.id) return true;
                      return !selectedPlanIds.includes(p.uid);
                    });
                    return (
                      <>
                        <select
                          value={plan.id}
                          onChange={(e) =>
                            handlePlanChange(plan.id, e.target.value)
                          }
                          className={cn(
                            'appearance-none bg-transparent h-6 w-[132px] cursor-pointer -my-2',
                            options.length > 1 && 'ml-2'
                          )}
                          disabled={options.length <= 1}
                        >
                          {options.map((p) => (
                            <option key={p.uid} value={p.uid} className="pr-4">
                              {p.name || p.marketingName || 'Untitled'}
                            </option>
                          ))}
                        </select>
                        {options.length > 1 && (
                          <span className="h-3 absolute right-0 pointer-events-none -translate-y-1/2">
                            <ChevronDownIcon />
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              ))
            )}
            {Array.from({ length: 3 - selectedPlans.length }, (_, index) => (
              <div key={`empty-plan-name-${index}`} />
            ))}
          </div>

          {comparisonData.map(({ label, values }) => (
            <div
              key={label}
              className="grid grid-cols-[minmax(200px,1fr)_repeat(3,minmax(120px,1fr))] gap-4 py-3 items-center"
            >
              <div className="font-medium text-xs text-gray-700">{label}</div>
              {selectedPlans.map((plan) => (
                <div key={plan.id + label} className="text-center text-sm">
                  {values[plan.id] || '—'}
                </div>
              ))}
              {Array.from({ length: 3 - selectedPlans.length }, (_, index) => (
                <div key={`empty-comparison-${label}-${index}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
